/**
 * Consultations adapter — mirrors the old-UI ai-consultant service so all
 * four AI Consultant screens (dashboard, new case wizard, workspace, report)
 * can be ported unchanged.
 *
 * Endpoints:
 *   POST   /v1/consultations                          (multipart FormData)
 *   GET    /v1/consultations?...filters
 *   GET    /v1/consultations/:caseId
 *   GET    /v1/consultations/:caseId?poll=1
 *   GET    /v1/consultations/:caseId/full-view
 *   POST   /v1/consultations/:caseId/analyze[?force=true][&async=true]
 *   PATCH  /v1/consultations/:caseId/archive
 *   GET    /v1/consultations/:caseId/chat
 *   POST   /v1/consultations/:caseId/chat  { message }
 *   GET    /v1/ai/jobs/:queue/:jobId
 */
import { api } from "@/lib/api";

function paramsFrom(filters = {}) {
  const params = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== "") params[k] = s;
  }
  return params;
}

export async function listConsultations(filters = {}) {
  const { data } = await api.get("/consultations", { params: paramsFrom(filters) });
  return data;
}

export async function getConsultation(caseId, options = {}) {
  const params = {};
  if (options.poll) params.poll = "1";
  const { data } = await api.get(`/consultations/${encodeURIComponent(caseId)}`, { params });
  return data;
}

export async function getConsultationFullView(caseId) {
  const { data } = await api.get(`/consultations/${encodeURIComponent(caseId)}/full-view`);
  return data;
}

export async function getConsultationReferral(caseId) {
  const { data } = await api.get(`/consultations/${encodeURIComponent(caseId)}/referral`);
  return data;
}

export async function createConsultation(formData) {
  const { data } = await api.post("/consultations", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function analyzeConsultation(caseId, options = {}) {
  const params = {};
  if (options.force) params.force = "true";
  if (options.async) params.async = "true";
  const { data } = await api.post(`/consultations/${encodeURIComponent(caseId)}/analyze`, {}, { params });
  return data;
}

export async function archiveConsultation(caseId) {
  const { data } = await api.patch(`/consultations/${encodeURIComponent(caseId)}/archive`, {});
  return data;
}

export async function getConsultationChatHistory(caseId) {
  const { data } = await api.get(`/consultations/${encodeURIComponent(caseId)}/chat`);
  return data;
}

export async function sendConsultationChat(caseId, message) {
  const { data } = await api.post(`/consultations/${encodeURIComponent(caseId)}/chat`, { message });
  return data;
}

export async function getAiJobStatus(queue, jobId) {
  const { data } = await api.get(`/ai/jobs/${encodeURIComponent(queue)}/${encodeURIComponent(jobId)}`);
  return data;
}

/**
 * Poll `getConsultation(caseId, { poll: true })` until it has an AI-generated
 * clinical report or `maxAttempts` is reached. Resolves with the final case.
 */
export async function pollConsultationAnalysis(caseId, {
  maxAttempts = 30,
  intervalMs = 3000,
  onTick,
} = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    const record = await getConsultation(caseId, { poll: true });
    if (onTick) {
      try { onTick(record, i); } catch { /* ignore */ }
    }
    const status = record?.analysis_status || record?.status;
    const hasReport = !!(record?.clinical_report || record?.ai_report || record?.analysis?.report);
    if (hasReport || status === "completed" || status === "failed") {
      return record;
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  return getConsultation(caseId);
}
